import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  MessageSquare, 
  Mail, 
  HelpCircle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  FileText
} from 'lucide-react';

/**
 * Help & Support Section for Account/Settings tab
 * 
 * Includes:
 * - Feedback button (opens FeedbackDialog)
 * - Contact info (support email)
 * - FAQs accordion (role-specific)
 */

// Vendor-specific FAQs
const vendorFaqs = [
  {
    question: "Do I need a separate account to book services as a customer?",
    answer: "No. One Celey account can be used as both a customer and a service provider. You can switch between Customer and Provider modes from your Account tab — no second login is required."
  },
  {
    question: "How do payments and payouts work?",
    answer: "When a customer books your service, they pay a 50% deposit upfront. The remaining 50% is collected after you mark the job as complete. Payouts are sent to your connected Stripe account within 2-7 business days. You keep 97% of your earnings — a 3% platform fee applies to completed bookings."
  },
  {
    question: "How can I get more visibility and inquiries?",
    answer: "Complete your profile with high-quality photos and a detailed bio. Keep your calendar updated and respond to inquiries quickly. Positive reviews from customers also boost your visibility in search results."
  },
  {
    question: "How do cancellations work?",
    answer: "If you need to cancel a booking, do so as early as possible through the app. Repeated cancellations may affect your visibility. If a customer cancels, any deposit paid will be handled according to your cancellation policy."
  },
  {
    question: "What happens if my listing is inactive?",
    answer: "Inactive listings won't appear in customer searches. To reactivate, go to your profile settings and update your availability status to 'Available'. Make sure your calendar is current and your Stripe account is connected."
  },
  {
    question: "Why do I see 'The KRS Group LLC' in Stripe?",
    answer: "CeleyApp is operated by The KRS Group LLC - this is our legal business entity. Your bank details are securely handled by Stripe. We never see your banking information directly."
  }
];

// Customer-specific FAQs
const customerFaqs = [
  {
    question: "Do I need a new account to become a service provider?",
    answer: "No. You can become a service provider using your existing Celey account. Just complete the provider signup process, and you'll be able to switch between Customer and Provider modes from your Account tab."
  },
  {
    question: "How do I book a service provider?",
    answer: "Browse providers, send an inquiry with your event details, and wait for their proposal. Once you receive a proposal you like, accept it and sign the service agreement. Then pay the 50% deposit to confirm your booking."
  },
  {
    question: "How do deposits and payments work?",
    answer: "You'll pay a 50% deposit when you confirm a booking. The remaining 50% is due after the service is completed and you've had a chance to review the work. All payments are processed securely through Stripe."
  },
  {
    question: "What if I need to cancel my booking?",
    answer: "Contact the provider as soon as possible if you need to cancel. Cancellation policies vary by provider - check the service agreement for details. Deposits may be partially or fully refundable depending on timing and the provider's policy."
  },
  {
    question: "How does messaging work?",
    answer: "You can message providers directly through the app after sending an inquiry. All communication is kept in one place so you have a record of your conversations. Providers typically respond within 24-48 hours."
  },
  {
    question: "What if I have an issue with a provider?",
    answer: "First, try to resolve the issue directly with the provider through messaging. If you can't reach a resolution, you can submit a dispute through the app or contact our support team. We're here to help mediate and ensure a fair outcome."
  }
];

const FAQAccordion = ({ faqs }) => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="space-y-2">
      {faqs.map((faq, index) => (
        <div 
          key={index} 
          className="border border-gray-200 rounded-lg overflow-hidden"
        >
          <button
            onClick={() => toggleFaq(index)}
            className="w-full flex items-center justify-between p-3 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <span className="text-sm font-medium text-gray-900 pr-4">{faq.question}</span>
            {openIndex === index ? (
              <ChevronUp className="w-4 h-4 text-gray-500 flex-shrink-0" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
            )}
          </button>
          {openIndex === index && (
            <div className="p-3 bg-white border-t border-gray-100">
              <p className="text-sm text-gray-600">{faq.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const HelpSupportSection = ({ 
  userRole = 'customer', 
  onFeedbackClick,
  supportEmail = 'celeyapp@gmail.com',
  embedded = false  // When true, render without outer Card wrapper
}) => {
  const navigate = useNavigate();
  const faqs = userRole === 'vendor' ? vendorFaqs : customerFaqs;

  const content = (
    <div className={embedded ? "p-4 space-y-5" : "space-y-5"}>
      {/* Feedback */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-emerald-600" />
          Feedback
        </h4>
        <p className="text-xs text-gray-500 mb-2">
          Help us improve Celey by sharing your thoughts and suggestions.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onFeedbackClick}
            className="w-full sm:w-auto"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Send Feedback
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/my-feedback')}
            className="w-full sm:w-auto text-gray-600"
          >
            <FileText className="w-4 h-4 mr-2" />
            My Feedback
          </Button>
        </div>
      </div>

      {/* Contact */}
      <div className="pt-3 border-t border-gray-100">
        <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
          <Mail className="w-4 h-4 text-blue-600" />
          Contact Support
        </h4>
        <p className="text-xs text-gray-500 mb-2">
          For account issues, payment questions, or technical support.
        </p>
        <a 
          href={`mailto:${supportEmail}`}
          className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 hover:underline"
        >
          <Mail className="w-4 h-4" />
          {supportEmail}
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* FAQs */}
      <div className="pt-3 border-t border-gray-100">
        <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
          <HelpCircle className="w-4 h-4 text-amber-600" />
          Frequently Asked Questions
        </h4>
        <FAQAccordion faqs={faqs} />
      </div>
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <Card className="mt-6">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <HelpCircle className="w-5 h-5 text-blue-600" />
          Help & Support
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {content}
      </CardContent>
    </Card>
  );
};

export default HelpSupportSection;
